import { useState, useEffect, useMemo } from 'react'
import { logger } from 'services/logger'

const SMART_SCRIPT_URL =
  'https://onelinksmartscript.appsflyer.com/onelink-smart-script-latest.js'

export type OneLinkConfig = {
  oneLinkUrl: string
  mediaSource?: string
  // Omit to use the shortlink's own attribution presets.
  campaign?: string
  deepLinkValue?: string
}

// Inject the script once per session; cache generated links per config.
let scriptPromise: Promise<void> | null = null
const urlCache = new Map<string, string>()

function loadSmartScript(): Promise<void> {
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = SMART_SCRIPT_URL
      script.onload = () => resolve()
      script.onerror = () => {
        scriptPromise = null
        reject(new Error('Failed to load AppsFlyer smart script'))
      }
      document.head.appendChild(script)
    })
  }
  return scriptPromise
}

export function useOneLinkUrl(
  config: OneLinkConfig,
  isEnabled = true
): string | null {
  const { oneLinkUrl, mediaSource = 'web', campaign, deepLinkValue } = config
  const cacheKey = useMemo(
    () =>
      JSON.stringify({
        oneLinkUrl,
        mediaSource,
        campaign,
        deepLinkValue,
      }),
    [oneLinkUrl, mediaSource, campaign, deepLinkValue]
  )
  const [url, setUrl] = useState<string | null>(
    () => urlCache.get(cacheKey) ?? null
  )

  useEffect(() => {
    if (!isEnabled || urlCache.has(cacheKey)) return

    let cancelled = false
    loadSmartScript()
      .then(() => {
        if (cancelled) return
        if (!window.AF_SMART_SCRIPT) {
          logger.warning('AF_SMART_SCRIPT not found on window')
          return
        }

        const result = window.AF_SMART_SCRIPT.generateOneLinkURL({
          oneLinkURL: oneLinkUrl,
          afParameters: {
            mediaSource: { defaultValue: mediaSource },
            afCustom: [{ paramKey: 'af_ss_ui', defaultValue: 'true' }],
            ...(campaign && { campaign: { defaultValue: campaign } }),
            ...(deepLinkValue && {
              deepLinkValue: { defaultValue: deepLinkValue },
            }),
          },
        })

        const clickURL = result?.clickURL
        if (clickURL) {
          urlCache.set(cacheKey, clickURL)
          setUrl(clickURL)
        } else {
          logger.error(
            'No clickURL found in result',
            new Error('Empty clickURL'),
            { result }
          )
        }
      })
      .catch((e) => {
        logger.error('Failed to generate OneLink URL', e)
      })

    return () => {
      cancelled = true
    }
  }, [isEnabled, cacheKey, oneLinkUrl, mediaSource, campaign, deepLinkValue])

  return url
}
